import { useEffect } from 'react';
import { useTranslations } from '../hooks/useTranslations';
import { getDubbingLandingSeo } from '../content/dubbingLanding';
import { DUBBING_LANDING_PATH, getLocalizedPath } from '../utils/localizedRoutes';

// SEO мета-теги для каждого языка
const seoData: Record<string, {
  title: string;
  description: string;
  keywords: string;
  ogLocale: string;
}> = {
  en: {
    title: 'VEVI - AI Voice Translation & Dubbing for Videos | Replace Audio in Any Language',
    description: 'Transform your videos with AI voice cloning and translation. Replace audio in 28+ languages while preserving your natural voice. Pay only $0.0314/second for professional video dubbing. Fast processing in 5-10 minutes.',
    keywords: 'AI voice translation, video dubbing, voice cloning, multilingual video, audio replacement, video localization, AI dubbing, voice-over translation, video translation service',
    ogLocale: 'en_US'
  },
  es: {
    title: 'VEVI - Traducción y Doblaje de Voz con IA para Videos | Reemplazar Audio en Cualquier Idioma',
    description: 'Transforma tus videos con clonación y traducción de voz con IA. Reemplaza el audio en 28+ idiomas preservando tu voz natural. Paga solo $0.0314/segundo por doblaje profesional de video. Procesamiento rápido en 5-10 minutos.',
    keywords: 'traducción de voz IA, doblaje de video, clonación de voz, video multilingüe, reemplazo de audio, localización de video, doblaje IA, traducción de voz en off, servicio de traducción de video',
    ogLocale: 'es_ES'
  },
  ru: {
    title: 'VEVI - Перевод и озвучка видео с помощью ИИ | Замена аудио на любом языке',
    description: 'Преобразуйте свои видео с помощью клонирования и перевода голоса с ИИ. Замените аудио на 28+ языках, сохраняя ваш естественный голос. Платите только $0.0314/секунду за профессиональное озвучивание видео. Быстрая обработка за 5-10 минут.',
    keywords: 'перевод голоса ИИ, озвучка видео, клонирование голоса, многоязычное видео, замена аудио, локализация видео, озвучка ИИ, перевод голоса за кадром, сервис перевода видео',
    ogLocale: 'ru_RU'
  },
  fr: {
    title: 'VEVI - Traduction et Doublage Vocal IA pour Vidéos | Remplacer l\'Audio dans Toute Langue',
    description: 'Transformez vos vidéos avec le clonage et la traduction vocale IA. Remplacez l\'audio en 28+ langues tout en préservant votre voix naturelle. Payez seulement $0.0314/seconde pour le doublage vidéo professionnel. Traitement rapide en 5-10 minutes.',
    keywords: 'traduction vocale IA, doublage vidéo, clonage vocal, vidéo multilingue, remplacement audio, localisation vidéo, doublage IA, traduction voix off, service de traduction vidéo',
    ogLocale: 'fr_FR'
  },
  de: {
    title: 'VEVI - KI-Sprachübersetzung & Synchronisation für Videos | Audio in Jeder Sprache Ersetzen',
    description: 'Transformieren Sie Ihre Videos mit KI-Stimmenklonierung und -übersetzung. Ersetzen Sie Audio in 28+ Sprachen und bewahren Sie Ihre natürliche Stimme. Zahlen Sie nur $0.0314/Sekunde für professionelle Videosynchronisation. Schnelle Verarbeitung in 5-10 Minuten.',
    keywords: 'KI-Sprachübersetzung, Videosynchronisation, Stimmenklonierung, mehrsprachiges Video, Audioersetzung, Videolokalisierung, KI-Synchronisation, Voice-Over-Übersetzung, Videoubersetzungsdienst',
    ogLocale: 'de_DE'
  },
  it: {
    title: 'VEVI - Traduzione e Doppiaggio Vocale IA per Video | Sostituisci Audio in Qualsiasi Lingua',
    description: 'Trasforma i tuoi video con clonazione e traduzione vocale IA. Sostituisci l\'audio in 28+ lingue preservando la tua voce naturale. Paga solo $0.0314/secondo per il doppiaggio video professionale. Elaborazione rapida in 5-10 minuti.',
    keywords: 'traduzione vocale IA, doppiaggio video, clonazione vocale, video multilingue, sostituzione audio, localizzazione video, doppiaggio IA, traduzione voice-over, servizio traduzione video',
    ogLocale: 'it_IT'
  },
  pt: {
    title: 'VEVI - Tradução e Dublagem de Voz com IA para Vídeos | Substituir Áudio em Qualquer Idioma',
    description: 'Transforme seus vídeos com clonagem e tradução de voz com IA. Substitua o áudio em 28+ idiomas preservando sua voz natural. Pague apenas $0.0314/segundo para dublagem profissional de vídeo. Processamento rápido em 5-10 minutos.',
    keywords: 'tradução de voz IA, dublagem de vídeo, clonagem de voz, vídeo multilíngue, substituição de áudio, localização de vídeo, dublagem IA, tradução de voz em off, serviço de tradução de vídeo',
    ogLocale: 'pt_BR'
  },
  pl: {
    title: 'VEVI - Tłumaczenie i Dubbing Głosu AI dla Filmów | Zamień Audio w Dowolnym Języku',
    description: 'Przekształć swoje filmy dzięki klonowaniu i tłumaczeniu głosu AI. Zamień audio w 28+ językach zachowując swój naturalny głos. Zapłać tylko $0.0314/sekundę za profesjonalny dubbing wideo. Szybkie przetwarzanie w 5-10 minut.',
    keywords: 'tłumaczenie głosu AI, dubbing wideo, klonowanie głosu, wielojęzyczne wideo, zamiana audio, lokalizacja wideo, dubbing AI, tłumaczenie voice-over, usługa tłumaczenia wideo',
    ogLocale: 'pl_PL'
  },
  ja: {
    title: 'VEVI - AI音声翻訳・動画吹き替え | あらゆる言語で音声を置き換え',
    description: 'AI音声クローニングと翻訳で動画を変換。自然な声を保ちながら28以上の言語で音声を置き換え。プロフェッショナルな動画吹き替えは1秒あたり$0.0314のみ。5-10分で高速処理。',
    keywords: 'AI音声翻訳, 動画吹き替え, 音声クローニング, 多言語動画, 音声置換, 動画ローカライゼーション, AI吹き替え, ボイスオーバー翻訳, 動画翻訳サービス',
    ogLocale: 'ja_JP'
  },
  ko: {
    title: 'VEVI - AI 음성 번역 및 비디오 더빙 | 모든 언어로 오디오 교체',
    description: 'AI 음성 복제 및 번역으로 비디오를 변환하세요. 자연스러운 목소리를 유지하면서 28개 이상의 언어로 오디오를 교체합니다. 전문 비디오 더빙은 초당 $0.0314만 지불하세요. 5-10분 내 빠른 처리.',
    keywords: 'AI 음성 번역, 비디오 더빙, 음성 복제, 다국어 비디오, 오디오 교체, 비디오 현지화, AI 더빙, 보이스오버 번역, 비디오 번역 서비스',
    ogLocale: 'ko_KR'
  },
  zh: {
    title: 'VEVI - AI语音翻译和视频配音 | 用任何语言替换音频',
    description: '使用AI语音克隆和翻译转换您的视频。在保持自然声音的同时，用28+种语言替换音频。专业视频配音仅需每秒$0.0314。5-10分钟快速处理。',
    keywords: 'AI语音翻译, 视频配音, 语音克隆, 多语言视频, 音频替换, 视频本地化, AI配音, 画外音翻译, 视频翻译服务',
    ogLocale: 'zh_CN'
  }
};

type SEOPageKey = "home" | "dubbingLanding";
type SEOOverride = {
  title: string;
  description: string;
  keywords: string;
};

function toVeviCanonicalPath(path: string): string {
  // Cloudflare Pages on vevi.monster serves localized app routes without .html.
  return path.replace(/\.html$/i, '');
}

export function SEOHead({
  pageKey = "home",
  overrideSeo,
  canonicalPathOverride,
  canonicalPathIsLocalized = false,
}: {
  pageKey?: SEOPageKey;
  overrideSeo?: SEOOverride;
  canonicalPathOverride?: string;
  canonicalPathIsLocalized?: boolean;
}) {
  const { currentLanguage } = useTranslations();
  const homeSeo = seoData[currentLanguage] || seoData.en;
  const dubbingSeo = getDubbingLandingSeo(currentLanguage);
  const baseSeo =
    pageKey === "dubbingLanding"
      ? {
          ...homeSeo,
          title: dubbingSeo.title,
          description: dubbingSeo.description,
          keywords: dubbingSeo.keywords,
        }
      : homeSeo;
  const seo = overrideSeo ? { ...baseSeo, ...overrideSeo } : baseSeo;

  useEffect(() => {
    // Обновляем title
    document.title = seo.title;

    // Обновляем lang атрибут у html
    document.documentElement.lang = currentLanguage;

    // Обновляем или создаем meta теги
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Обновляем основные мета-теги
    updateMetaTag('title', seo.title);
    updateMetaTag('description', seo.description);
    updateMetaTag('keywords', seo.keywords);

    // Обновляем Open Graph теги
    updateMetaTag('og:title', seo.title, true);
    updateMetaTag('og:description', seo.description, true);
    updateMetaTag('og:locale', seo.ogLocale, true);

    // Обновляем Twitter теги
    updateMetaTag('twitter:title', seo.title, true);
    updateMetaTag('twitter:description', seo.description, true);

    // Обновляем canonical URL с языковой HTML-страницей
    const canonicalBasePath = canonicalPathOverride || (pageKey === "dubbingLanding" ? DUBBING_LANDING_PATH : "/");
    const localizedCanonicalPath = canonicalPathIsLocalized
      ? canonicalBasePath
      : getLocalizedPath(canonicalBasePath, currentLanguage);
    const canonicalPath = toVeviCanonicalPath(localizedCanonicalPath);
    const canonicalUrl = canonicalPath === '/'
      ? 'https://vevi.monster'
      : `https://vevi.monster${canonicalPath}`;
    
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Обновляем og:url
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('twitter:url', canonicalUrl, true);

  }, [currentLanguage, seo, pageKey, canonicalPathOverride, canonicalPathIsLocalized]);

  return null;
}
